import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as Avatar } from "./IconUmmahfy.svg";
import { slugifyOrgName } from "./utils/slug";

/**
 * Compact organization card showing profile picture + name.
 * Links to the public organization page (/organization/:slug).
 *
 * Props:
 *   org: { name, profilePicture? }
 *   className?: string
 */
function OrgCard({ org, className = "" }) {
  // Track image load failures so a dead URL falls back to the avatar
  // glyph instead of showing the browser's broken-image icon + alt text.
  const [imgFailed, setImgFailed] = useState(false);

  // Reset the failure flag if the org (or its picture URL) changes,
  // so swapping to a new org actually retries the new URL.
  useEffect(() => {
    setImgFailed(false);
  }, [org?.profilePicture]);

  if (!org?.name) return null;

  const slug = slugifyOrgName(org.name);
  const trimmedPic =
    typeof org.profilePicture === "string" ? org.profilePicture.trim() : "";
  const showImg = trimmedPic.length > 0 && !imgFailed;

  return (
    <Link
      to={`/organization/${slug}`}
      className={`org-card ${className}`.trim()}
      aria-label={`Open ${org.name}'s page`}
    >
      <div className="org-card-pic-wrap">
        {showImg ? (
          <img
            src={trimmedPic}
            alt={`${org.name} logo`}
            className="org-card-pic"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <Avatar className="org-card-pic" />
        )}
      </div>
      <p className="org-card-name" title={org.name}>
        {org.name}
      </p>
    </Link>
  );
}

export default OrgCard;
